import { ErreurApi, type Client } from '../../api/client.js';
import { vueClient } from './modele.js';
import type { Argent, Calendrier, CorpsRegles, DonneesEquipe, Fournisseur, VueClient, VueEquipe } from './types.js';

export interface SourceFacturation {
  charger: (dossierId: string) => Promise<DonneesEquipe>;
  enregistrerRegles: (dossierId: string, corps: CorpsRegles) => Promise<void>;
  creerEcheancier: (dossierId: string, personId: string, total: Argent, count: number) => Promise<void>;
  emettreFacture: (dossierId: string, payerPersonId: string) => Promise<void>;
  envoyerLiens: (dossierId: string, installmentIds: readonly string[], provider: Fournisseur) => Promise<void>;
}

/** Accès aux routes du contrat pour D08. Les écritures passent toutes par des routes auditées côté serveur. */
export function creerSource(client: Client): SourceFacturation {
  return {
    async charger(dossierId) {
      const parametres = { dossierId };
      const [facturation, collecte] = await Promise.allSettled([
        client.appeler<VueEquipe>('facturation.dossier', { parametres }),
        client.appeler<Calendrier>('facturation.collecte', { parametres }),
      ]);
      // Écart C10 : un 404 de l'une des deux lectures veut dire « pas encore de règles » ou « pas encore de date de signature »,
      // tant que le dossier existe. Toute autre erreur remonte telle quelle (refus, réseau, serveur).
      const absent = (r: PromiseSettledResult<unknown>): boolean =>
        r.status === 'rejected' && r.reason instanceof ErreurApi && r.reason.code === 'NOT_FOUND';
      for (const r of [facturation, collecte]) if (r.status === 'rejected' && !absent(r)) throw r.reason;
      // Les deux absentes : seul le dossier lui-même dit s'il existe (sinon « introuvable », ARB-FACT-07).
      if (facturation.status === 'rejected' && collecte.status === 'rejected') await client.appeler('dossiers.lire', { parametres });
      return {
        facturation: facturation.status === 'fulfilled' ? facturation.value : null,
        collecte: collecte.status === 'fulfilled' ? collecte.value : null,
      };
    },
    async enregistrerRegles(dossierId, corps) {
      await client.appeler('facturation.regles', { parametres: { dossierId }, corps });
    },
    async creerEcheancier(dossierId, personId, total, count) {
      await client.appeler('facturation.echeancier', { parametres: { dossierId }, corps: { personId, total, count } });
    },
    async emettreFacture(dossierId, payerPersonId) {
      await client.appeler('facturation.factures.emettre', { parametres: { dossierId }, corps: { payerPersonId } });
    },
    async envoyerLiens(dossierId, installmentIds, provider) {
      await client.appeler('paiements.liens', { parametres: { dossierId }, corps: { installmentIds: [...installmentIds], provider } });
    },
  };
}

/** M06 : lit `portail.paiement` et ne garde que la vue client (jamais une somme réglée). */
export async function chargerPaiementClient(client: Client): Promise<VueClient> {
  return vueClient(await client.appeler<unknown>('portail.paiement'));
}
